import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { productsAPI, categoriesAPI } from '../services/api';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import ProductCard from './ProductCard';
import { useCart } from '../contexts/CartContext';
import { Button } from './ui/button';
import { ShoppingCart, Filter, Syringe, SprayCan, Pill, Package, FlaskConical, Shield, Beaker, Truck, Award, Sparkles } from 'lucide-react';
import { trackSearch } from '../utils/analytics';

const ProductsPage = () => {
  const { t, currentLanguage } = useLanguage();
  const { addToCart, loading: cartLoading } = useCart();
  const { formatPrice } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const selectedCategory = searchParams.get('category') || 'all';
  const selectedProductType = searchParams.get('type') || 'all';

  const setSelectedCategory = (category) => {
    const newParams = new URLSearchParams(searchParams);
    if (category === 'all') {
      newParams.delete('category');
    } else {
      newParams.set('category', category);
    }
    setSearchParams(newParams, { replace: true });
  };

  const setSelectedProductType = (type) => {
    const newParams = new URLSearchParams(searchParams);
    if (type === 'all') {
      newParams.delete('type');
    } else {
      newParams.set('type', type);
    }
    setSearchParams(newParams, { replace: true });
  };

  const clearAllFilters = () => {
    setSearchParams({}, { replace: true });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsData, categoriesData, productTypesData] = await Promise.all([
          productsAPI.getAll(),
          categoriesAPI.getAll(),
          productsAPI.getProductTypes()
        ]);
        setProducts(productsData);
        setCategories(categoriesData);
        setProductTypes(productTypesData);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Resolve `?category_slug=X` → `?category={uuid}` (iter-53). The IIFE
  // in index.html redirects legacy blog-internal-link patterns like
  // `/products/category/weight-loss` to `/{lang}/products?category_slug=weight-loss`.
  // Here we swap the slug for the canonical category UUID so the existing
  // filter wiring (which keys off UUIDs) Just Works.
  useEffect(() => {
    const slug = searchParams.get('category_slug');
    if (!slug || categories.length === 0) return;
    const match = categories.find((c) => (c.slug || '').toLowerCase() === slug.toLowerCase());
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('category_slug');
    if (match) newParams.set('category', match.id);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, categories, setSearchParams]);

  const getProductName = (product) => {
    return product.name[currentLanguage] || product.name.en || product.name;
  };

  // 9-language category name overlay (DB only ships en/es/ru; this widens
  // the catalogue chrome to all 9 supported locales until categories are
  // backfilled in the DB).
  const CATEGORY_NAME_OVERLAY = {
    'Basic & Specialized Peptides': {
      en: 'Basic & Specialized Peptides', es: 'Péptidos Básicos y Especializados',
      ru: 'Базовые и специализированные пептиды',
      de: 'Basis- & Spezialpeptide', fr: 'Peptides de base et spécialisés',
      it: 'Peptidi base e specializzati', pl: 'Peptydy podstawowe i specjalistyczne',
      nl: 'Basis- & Gespecialiseerde Peptiden', pt: 'Peptídeos Básicos e Especializados',
      au: 'Basic & Specialized Peptides',
    },
    'NAD+ & Longevity': {
      en: 'NAD+ & Longevity', es: 'NAD+ y Longevidad', ru: 'NAD+ и долголетие',
      de: 'NAD+ & Langlebigkeit', fr: 'NAD+ et longévité',
      it: 'NAD+ e longevità', pl: 'NAD+ i długowieczność',
      nl: 'NAD+ & Langlevigheid', pt: 'NAD+ e Longevidade',
      au: 'NAD+ & Longevity',
    },
    'Weight Loss': {
      en: 'Weight Loss', es: 'Pérdida de Peso', ru: 'Снижение веса',
      de: 'Gewichtsmanagement', fr: 'Gestion du poids',
      it: 'Gestione del peso', pl: 'Zarządzanie wagą',
      nl: 'Gewichtsbeheersing', pt: 'Gestão de peso',
      au: 'Weight Management',
    },
    'SARMs & Stacks': {
      en: 'SARMs & Stacks', es: 'SARMs y Combinaciones', ru: 'SARMs и стеки',
      de: 'SARMs & Stacks', fr: 'SARMs et combinaisons',
      it: 'SARMs e combinazioni', pl: 'SARM i zestawy',
      nl: 'SARMs & Stacks', pt: 'SARMs e Combinações',
      au: 'SARMs & Stacks',
    },
    'Advanced Formulas': {
      en: 'Advanced Formulas', es: 'Fórmulas Avanzadas', ru: 'Продвинутые формулы',
      de: 'Erweiterte Formeln', fr: 'Formules avancées',
      it: 'Formule avanzate', pl: 'Zaawansowane formuły',
      nl: 'Geavanceerde Formules', pt: 'Fórmulas Avançadas',
      au: 'Advanced Formulas',
    },
    'Bioregulators & Extracts': {
      en: 'Bioregulators & Extracts', es: 'Biorreguladores y Extractos',
      ru: 'Биорегуляторы и экстракты',
      de: 'Bioregulatoren & Extrakte', fr: 'Biorégulateurs et extraits',
      it: 'Bioregolatori ed estratti', pl: 'Bioregulatory i ekstrakty',
      nl: 'Bioregulatoren & Extracten', pt: 'Biorreguladores e Extratos',
      au: 'Bioregulators & Extracts',
    },
    'Exosomes & Special Products': {
      en: 'Exosomes & Special Products', es: 'Exosomas y Productos Especiales',
      ru: 'Экзосомы и специальные продукты',
      de: 'Exosomen & Spezialprodukte', fr: 'Exosomes et produits spéciaux',
      it: 'Esosomi e prodotti speciali', pl: 'Egzosomy i produkty specjalne',
      nl: 'Exosomen & Speciale Producten', pt: 'Exossomos e Produtos Especiais',
      au: 'Exosomes & Special Products',
    },
    'Specialized Complexes': {
      en: 'Specialized Complexes', es: 'Complejos Especializados',
      ru: 'Специализированные комплексы',
      de: 'Spezialisierte Komplexe', fr: 'Complexes spécialisés',
      it: 'Complessi specializzati', pl: 'Wyspecjalizowane kompleksy',
      nl: 'Gespecialiseerde Complexen', pt: 'Complexos Especializados',
      au: 'Specialized Complexes',
    },
  };

  const getCategoryName = (category) => {
    const en = category?.name?.en;
    const overlay = en && CATEGORY_NAME_OVERLAY[en];
    if (overlay && overlay[currentLanguage]) return overlay[currentLanguage];
    return category.name[currentLanguage] || category.name.en || category.name;
  };

  const getProductTypeName = (productType) => {
    if (!productType) return '';
    return productType[currentLanguage] || productType.en || productType;
  };

  // formatPrice provided by useCurrency()

  const hasVariants = (product) => product.variants && product.variants.length > 1;

  const getDisplayPrice = (product) => {
    if (product.variants && product.variants.length > 0) {
      const prices = product.variants.map(v => v.price);
      return Math.min(...prices);
    }
    return product.price;
  };

  const getPriceLabel = (product) => {
    if (hasVariants(product)) {
      const fromText = currentLanguage === 'es' ? 'Desde' : 
                       currentLanguage === 'ru' ? 'От' : 'From';
      return `${fromText} ${formatPrice(getDisplayPrice(product))}`;
    }
    return formatPrice(getDisplayPrice(product));
  };

  const getProductTypeIcon = (typeName) => {
    const name = typeName?.toLowerCase() || '';
    if (name.includes('pen') || name.includes('ручка')) return <Syringe className="w-4 h-4" />;
    if (name.includes('spray') || name.includes('спрей')) return <SprayCan className="w-4 h-4" />;
    if (name.includes('capsule') || name.includes('cápsula') || name.includes('капсул')) return <Pill className="w-4 h-4" />;
    if (name.includes('vial') || name.includes('флакон')) return <FlaskConical className="w-4 h-4" />;
    return <Package className="w-4 h-4" />;
  };

  const handleAddToCart = async (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const cartProduct = { ...product };
    if (product.variants && product.variants.length > 0) {
      cartProduct.price = product.variants[0].price;
      cartProduct.selectedDosage = product.variants[0].dosage;
    }
    
    await addToCart(cartProduct, 1);
  };

  // Search filter (iter-53): supports `?search=X` URL param so legacy blog
  // internal-links like `/products/tirzepatide` (redirected by the IIFE to
  // `/{lang}/products?search=tirzepatide`) actually narrow the listing.
  // Case-insensitive match against EN name, description, and tags so the
  // single query works across locales.
  const searchQuery = (searchParams.get('search') || '').trim().toLowerCase();

  const filteredProducts = products.filter(p => {
    const categoryMatch = selectedCategory === 'all' || p.category_id === selectedCategory;
    const typeMatch = selectedProductType === 'all' || 
      (p.product_type && (
        p.product_type.en === selectedProductType ||
        p.product_type.es === selectedProductType ||
        p.product_type.ru === selectedProductType
      ));
    if (!categoryMatch || !typeMatch) return false;
    if (!searchQuery) return true;
    const enName = (p.name?.en || p.name || '').toString().toLowerCase();
    const enDesc = (p.description?.en || '').toString().toLowerCase();
    const tags = ((p.tags || []).join(' ')).toLowerCase();
    return enName.includes(searchQuery) || enDesc.includes(searchQuery) || tags.includes(searchQuery);
  });

  // ---- Analytics: fire view_search_results when user submits a search ----
  // Fires whenever the URL search param changes (after products load so we
  // have a real result count). Used by Google Ads' smart-bidding model to
  // distinguish high-intent (search → result → click) from low-intent
  // traffic. Also lets the user see empty-search funnels in GA4.
  useEffect(() => {
    if (!loading && searchQuery) {
      try { trackSearch(searchQuery, filteredProducts.length); }
      catch (_) { /* analytics never blocks UX */ }
    }
  }, [searchQuery, loading, filteredProducts.length]);

  // ---- Soft-404 mitigation: noindex empty /products?search=X URLs ------
  // GSC was flagging `/products?search=<irrelevant-term>` URLs as Soft-404s
  // because Googlebot saw a near-empty page. Empty search-result pages
  // are never useful for indexing, emit `noindex, follow` so they get
  // dropped from the index while still passing link equity along. On
  // unmount (or when results reappear), restore `index, follow`.
  const isEmptySearchPage = !loading && Boolean(searchQuery) && filteredProducts.length === 0;
  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]');
    const googlebot = document.querySelector('meta[name="googlebot"]');
    const prevR = robots?.getAttribute('content');
    const prevG = googlebot?.getAttribute('content');
    if (isEmptySearchPage) {
      if (robots) robots.setAttribute('content', 'noindex, follow');
      if (googlebot) googlebot.setAttribute('content', 'noindex, follow');
    }
    return () => {
      if (robots && prevR) robots.setAttribute('content', prevR);
      if (googlebot && prevG) googlebot.setAttribute('content', prevG);
    };
  }, [isEmptySearchPage]);

  const productTypeLabels = {
    en: { all: 'All Types' }, es: { all: 'Todos los Tipos' },
    ru: { all: 'Все Типы' }, de: { all: 'Alle Typen' },
    fr: { all: 'Tous les Types' }, it: { all: 'Tutti i Tipi' },
    pl: { all: 'Wszystkie Typy' }, nl: { all: 'Alle Typen' },
    pt: { all: 'Todos os Tipos' }, au: { all: 'All Types' },
  };

  // 9-language UI chrome labels used across the products listing page.
  const P = {
    en: { filterByType: 'Filter by Type', filterByCategory: 'Filter by Category',
          allProducts: 'All Products', showing: (n) => `Showing ${n} products`,
          noResults: 'No products found with selected filters.', clear: 'Clear Filters',
          heroBadge: 'Premium Research Products', heroTitle1: 'Research-Grade', heroTitle2: 'Peptides',
          heroDesc: 'Discover our complete collection of peptides, SARMs, and performance compounds. Stabilized formulas with 99%+ purity and certificates of analysis included.',
          statProducts: 'Products', statPurity: 'Purity', statShipping: 'Shipping',
          cardPen: 'Pen Format', cardPenSub: 'Precise dosing',
          cardCertified: 'Certified', cardCertifiedSub: 'COA included',
          cardMulti: 'Multi-Format', cardMultiSub: 'Pen, Spray, Capsules',
          cardResearch: 'Research', cardResearchSub: 'Scientific use only' },
    es: { filterByType: 'Filtrar por Tipo', filterByCategory: 'Filtrar por Categoría',
          allProducts: 'Todos los Productos', showing: (n) => `Mostrando ${n} productos`,
          noResults: 'No se encontraron productos con los filtros seleccionados.', clear: 'Limpiar Filtros',
          heroBadge: 'Productos de Investigación Premium', heroTitle1: 'Péptidos de', heroTitle2: 'Grado Investigación',
          heroDesc: 'Descubre nuestra colección completa de péptidos, SARMs y compuestos de rendimiento. Fórmulas estabilizadas con pureza del 99%+ y certificados de análisis incluidos.',
          statProducts: 'Productos', statPurity: 'Pureza', statShipping: 'Envío',
          cardPen: 'Formato Pen', cardPenSub: 'Dosificación precisa',
          cardCertified: 'Certificado', cardCertifiedSub: 'COA incluido',
          cardMulti: 'Multi-Formato', cardMultiSub: 'Pen, Spray, Cápsulas',
          cardResearch: 'Investigación', cardResearchSub: 'Solo uso científico' },
    ru: { filterByType: 'Фильтр по Типу', filterByCategory: 'Фильтр по Категории',
          allProducts: 'Все Продукты', showing: (n) => `Показано ${n} товаров`,
          noResults: 'Продукты не найдены по выбранным фильтрам.', clear: 'Очистить Фильтры',
          heroBadge: 'Премиальные Исследовательские Продукты', heroTitle1: 'Пептиды', heroTitle2: 'Исследовательского Уровня',
          heroDesc: 'Откройте для себя нашу полную коллекцию пептидов, SARMs и соединений для производительности. Стабилизированные формулы с чистотой 99%+ и сертификатами анализа.',
          statProducts: 'Продуктов', statPurity: 'Чистота', statShipping: 'Доставка',
          cardPen: 'Формат Ручки', cardPenSub: 'Точная дозировка',
          cardCertified: 'Сертифицировано', cardCertifiedSub: 'COA включен',
          cardMulti: 'Мульти-Формат', cardMultiSub: 'Ручка, Спрей, Капсулы',
          cardResearch: 'Исследования', cardResearchSub: 'Только для науки' },
    de: { filterByType: 'Nach Typ filtern', filterByCategory: 'Nach Kategorie filtern',
          allProducts: 'Alle Produkte', showing: (n) => `${n} Produkte werden angezeigt`,
          noResults: 'Keine Produkte mit den gewählten Filtern gefunden.', clear: 'Filter zurücksetzen',
          heroBadge: 'Premium-Forschungsprodukte', heroTitle1: 'Peptide in', heroTitle2: 'Forschungsqualität',
          heroDesc: 'Entdecken Sie unsere vollständige Auswahl an Peptiden, SARMs und Wirkstoffkombinationen. Stabilisierte Formeln mit über 99 % Reinheit und beigefügten Analysezertifikaten.',
          statProducts: 'Produkte', statPurity: 'Reinheit', statShipping: 'Versand',
          cardPen: 'Pen-Format', cardPenSub: 'Präzise Dosierung',
          cardCertified: 'Zertifiziert', cardCertifiedSub: 'COA inklusive',
          cardMulti: 'Multi-Format', cardMultiSub: 'Pen, Spray, Kapseln',
          cardResearch: 'Forschung', cardResearchSub: 'Nur wissenschaftliche Nutzung' },
    fr: { filterByType: 'Filtrer par type', filterByCategory: 'Filtrer par catégorie',
          allProducts: 'Tous les produits', showing: (n) => `${n} produits affichés`,
          noResults: 'Aucun produit ne correspond aux filtres sélectionnés.', clear: 'Effacer les filtres',
          heroBadge: 'Produits de Recherche Premium', heroTitle1: 'Peptides', heroTitle2: 'de Recherche',
          heroDesc: 'Découvrez notre collection complète de peptides, SARMs et composés de performance. Formules stabilisées avec une pureté supérieure à 99 % et certificats d’analyse inclus.',
          statProducts: 'Produits', statPurity: 'Pureté', statShipping: 'Livraison',
          cardPen: 'Format Stylo', cardPenSub: 'Dosage précis',
          cardCertified: 'Certifié', cardCertifiedSub: 'COA inclus',
          cardMulti: 'Multi-Format', cardMultiSub: 'Stylo, Spray, Capsules',
          cardResearch: 'Recherche', cardResearchSub: 'Usage scientifique uniquement' },
    it: { filterByType: 'Filtra per tipo', filterByCategory: 'Filtra per categoria',
          allProducts: 'Tutti i prodotti', showing: (n) => `Visualizzati ${n} prodotti`,
          noResults: 'Nessun prodotto trovato con i filtri selezionati.', clear: 'Cancella filtri',
          heroBadge: 'Prodotti di Ricerca Premium', heroTitle1: 'Peptidi di', heroTitle2: 'Grado Ricerca',
          heroDesc: 'Scopri la nostra collezione completa di peptidi, SARMs e composti per la performance. Formulazioni stabilizzate con purezza oltre il 99% e certificati di analisi inclusi.',
          statProducts: 'Prodotti', statPurity: 'Purezza', statShipping: 'Spedizione',
          cardPen: 'Formato Penna', cardPenSub: 'Dosaggio preciso',
          cardCertified: 'Certificato', cardCertifiedSub: 'COA incluso',
          cardMulti: 'Multi-Formato', cardMultiSub: 'Penna, Spray, Capsule',
          cardResearch: 'Ricerca', cardResearchSub: 'Solo uso scientifico' },
    pl: { filterByType: 'Filtruj według typu', filterByCategory: 'Filtruj według kategorii',
          allProducts: 'Wszystkie produkty', showing: (n) => `Wyświetlanie ${n} produktów`,
          noResults: 'Nie znaleziono produktów spełniających wybrane filtry.', clear: 'Wyczyść filtry',
          heroBadge: 'Premium produkty badawcze', heroTitle1: 'Peptydy klasy', heroTitle2: 'badawczej',
          heroDesc: 'Odkryj naszą pełną kolekcję peptydów, SARM i związków na wydajność. Stabilizowane preparaty o czystości powyżej 99 % z dołączonymi certyfikatami analizy.',
          statProducts: 'Produkty', statPurity: 'Czystość', statShipping: 'Wysyłka',
          cardPen: 'Format Pen', cardPenSub: 'Precyzyjne dawkowanie',
          cardCertified: 'Certyfikowane', cardCertifiedSub: 'COA w zestawie',
          cardMulti: 'Multi-Format', cardMultiSub: 'Pen, Spray, Kapsułki',
          cardResearch: 'Badania', cardResearchSub: 'Tylko do celów naukowych' },
    nl: { filterByType: 'Filter op type', filterByCategory: 'Filter op categorie',
          allProducts: 'Alle producten', showing: (n) => `${n} producten weergegeven`,
          noResults: 'Geen producten gevonden met de geselecteerde filters.', clear: 'Filters wissen',
          heroBadge: 'Premium Onderzoeksproducten', heroTitle1: 'Onderzoeks-', heroTitle2: 'peptiden',
          heroDesc: 'Ontdek onze volledige collectie peptiden, SARMs en prestatieverbeterende verbindingen. Gestabiliseerde formules met meer dan 99% zuiverheid en analysecertificaten inbegrepen.',
          statProducts: 'Producten', statPurity: 'Zuiverheid', statShipping: 'Verzending',
          cardPen: 'Pen-Formaat', cardPenSub: 'Nauwkeurige dosering',
          cardCertified: 'Gecertificeerd', cardCertifiedSub: 'COA inbegrepen',
          cardMulti: 'Multi-Formaat', cardMultiSub: 'Pen, Spray, Capsules',
          cardResearch: 'Onderzoek', cardResearchSub: 'Alleen wetenschappelijk gebruik' },
    pt: { filterByType: 'Filtrar por tipo', filterByCategory: 'Filtrar por categoria',
          allProducts: 'Todos os produtos', showing: (n) => `Mostrando ${n} produtos`,
          noResults: 'Nenhum produto encontrado com os filtros selecionados.', clear: 'Limpar filtros',
          heroBadge: 'Produtos de Pesquisa Premium', heroTitle1: 'Peptídeos', heroTitle2: 'para Pesquisa',
          heroDesc: 'Descubra nossa coleção completa de peptídeos, SARMs e compostos de desempenho. Fórmulas estabilizadas com pureza acima de 99% e certificados de análise inclusos.',
          statProducts: 'Produtos', statPurity: 'Pureza', statShipping: 'Envio',
          cardPen: 'Formato Caneta', cardPenSub: 'Dosagem precisa',
          cardCertified: 'Certificado', cardCertifiedSub: 'COA incluído',
          cardMulti: 'Multi-Formato', cardMultiSub: 'Caneta, Spray, Cápsulas',
          cardResearch: 'Pesquisa', cardResearchSub: 'Apenas uso científico' },
    au: { filterByType: 'Filter by Type', filterByCategory: 'Filter by Category',
          allProducts: 'All Products', showing: (n) => `Showing ${n} products`,
          noResults: 'No products found with selected filters.', clear: 'Clear Filters',
          heroBadge: 'Premium Research Products', heroTitle1: 'Research-Grade', heroTitle2: 'Peptides',
          heroDesc: 'Discover our complete collection of peptides, SARMs, and performance compounds. Stabilised formulas with 99%+ purity and certificates of analysis included.',
          statProducts: 'Products', statPurity: 'Purity', statShipping: 'Shipping',
          cardPen: 'Pen Format', cardPenSub: 'Precise dosing',
          cardCertified: 'Certified', cardCertifiedSub: 'COA included',
          cardMulti: 'Multi-Format', cardMultiSub: 'Pen, Spray, Capsules',
          cardResearch: 'Research', cardResearchSub: 'Scientific use only' },
  };
  const LBL = P[currentLanguage] || P.en;

  if (loading) {
    return (
      <div className="min-h-screen bg-capsule-bg">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(12)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-white/50 rounded-3xl h-64 mb-4"></div>
                <div className="h-4 bg-white/50 rounded-xl mb-2"></div>
                <div className="h-4 bg-white/50 rounded-xl w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-capsule-bg">
      <SEOHead page="products" />
      <Header />

      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        {/* DNA Decoration */}
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="heroGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#heroGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#heroGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        {/* Floating spheres */}
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left side - Text content */}
            <div className="text-white">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
                <Beaker className="w-4 h-4 text-capsule-aqua" />
                <span className="text-sm font-medium text-capsule-aqua">
                  {LBL.heroBadge}
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                {LBL.heroTitle1}
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-capsule-teal to-capsule-aqua">
                  {LBL.heroTitle2}
                </span>
              </h1>
              
              <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-8 max-w-lg">
                {LBL.heroDesc}
              </p>
              
              {/* Quick stats */}
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-capsule-teal to-capsule-aqua flex items-center justify-center">
                    <FlaskConical className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold">143+</p>
                    <p className="text-gray-400 text-xs">{LBL.statProducts}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold">99%+</p>
                    <p className="text-gray-400 text-xs">{LBL.statPurity}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-capsule-coral to-capsule-salmon flex items-center justify-center">
                    <Truck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold">EU</p>
                    <p className="text-gray-400 text-xs">{LBL.statShipping}</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right side - Feature cards */}
            <div className="hidden md:grid grid-cols-2 gap-4">
              <div className="glass-card rounded-3xl p-5 border border-white/10 hover:bg-white/10 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-capsule-teal to-capsule-aqua flex items-center justify-center mb-4">
                  <Syringe className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-1">{LBL.cardPen}</h3>
                <p className="text-gray-600 text-sm">{LBL.cardPenSub}</p>
              </div>
              
              <div className="glass-card rounded-3xl p-5 border border-white/10 hover:bg-white/10 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-1">{LBL.cardCertified}</h3>
                <p className="text-gray-600 text-sm">{LBL.cardCertifiedSub}</p>
              </div>
              
              <div className="glass-card rounded-3xl p-5 border border-white/10 hover:bg-white/10 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-capsule-coral to-capsule-salmon flex items-center justify-center mb-4">
                  <SprayCan className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-1">{LBL.cardMulti}</h3>
                <p className="text-gray-600 text-sm">{LBL.cardMultiSub}</p>
              </div>
              
              <div className="glass-card rounded-3xl p-5 border border-white/10 hover:bg-white/10 transition-all">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-500 flex items-center justify-center mb-4">
                  <Beaker className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-gray-900 font-semibold mb-1">{LBL.cardResearch}</h3>
                <p className="text-gray-600 text-sm">{LBL.cardResearchSub}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>

      {/* Filters & Products */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Product Type Filter */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-capsule-teal" />
            <span className="text-sm font-medium text-gray-700">
              {LBL.filterByType}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedProductType === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedProductType('all')}
              className={`rounded-2xl ${selectedProductType === 'all' ? 'bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal border-0' : 'border-capsule-bg-dark hover:bg-capsule-teal/10 hover:border-capsule-teal'}`}
              size="sm"
              data-testid="filter-type-all"
            >
              <Package className="w-4 h-4 mr-1" />
              {productTypeLabels[currentLanguage]?.all || 'All Types'}
            </Button>
            {productTypes.map((type, index) => {
              const typeName = getProductTypeName(type);
              return (
                <Button
                  key={index}
                  variant={selectedProductType === type.en ? 'default' : 'outline'}
                  onClick={() => setSelectedProductType(type.en)}
                  className={`rounded-2xl ${selectedProductType === type.en ? 'bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal border-0' : 'border-capsule-bg-dark hover:bg-capsule-teal/10 hover:border-capsule-teal'}`}
                  size="sm"
                  data-testid={`filter-type-${type.en?.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {getProductTypeIcon(typeName)}
                  <span className="ml-1">{typeName}</span>
                </Button>
              );
            })}
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-capsule-coral" />
            <span className="text-sm font-medium text-gray-700">
              {LBL.filterByCategory}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
              className={`rounded-2xl ${selectedCategory === 'all' ? 'bg-gradient-to-r from-capsule-coral to-capsule-salmon hover:from-capsule-coral hover:to-capsule-coral border-0' : 'border-capsule-bg-dark hover:bg-capsule-coral/10 hover:border-capsule-coral'}`}
              data-testid="filter-category-all"
            >
              {LBL.allProducts} ({products.length})
            </Button>
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category.id)}
                className={`rounded-2xl ${selectedCategory === category.id ? 'bg-gradient-to-r from-capsule-coral to-capsule-salmon hover:from-capsule-coral hover:to-capsule-coral border-0' : 'border-capsule-bg-dark hover:bg-capsule-coral/10 hover:border-capsule-coral'}`}
                data-testid={`filter-category-${category.slug}`}
              >
                {getCategoryName(category)}
              </Button>
            ))}
          </div>
        </div>

        {/* Results count */}
        <div className="mb-6 flex items-center gap-2 text-sm text-gray-600">
          <Sparkles className="w-4 h-4 text-capsule-teal" />
          {LBL.showing(filteredProducts.length)}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* No results message, differentiated for empty search vs empty filters.
            Empty-search variant emits noindex (see useEffect above) and shows a
            primary "Browse all products" CTA so GSC doesn't classify the page
            as a Soft-404. Filter-empty variant just keeps the clear-filters
            button since the user can still see the filter UI to adjust. */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-12" data-testid="products-empty-state">
            <div className="glass-card inline-block rounded-3xl p-8 max-w-md">
              <FlaskConical className="w-12 h-12 text-capsule-teal mx-auto mb-4" />
              {searchQuery ? (
                <>
                  <h2 className="text-xl font-bold text-gray-900 mb-2" data-testid="empty-search-title">
                    {({
                      en: `No results for "${searchQuery}"`,
                      es: `Sin resultados para "${searchQuery}"`,
                      ru: `Ничего не найдено по запросу "${searchQuery}"`,
                      de: `Keine Ergebnisse für "${searchQuery}"`,
                      fr: `Aucun résultat pour "${searchQuery}"`,
                      it: `Nessun risultato per "${searchQuery}"`,
                      pl: `Brak wyników dla "${searchQuery}"`,
                      nl: `Geen resultaten voor "${searchQuery}"`,
                      pt: `Sem resultados para "${searchQuery}"`,
                    })[currentLanguage] || `No results for "${searchQuery}"`}
                  </h2>
                  <p className="text-gray-600 mb-6 text-sm" data-testid="empty-search-body">
                    {({
                      en: 'We couldn’t find any peptides matching your search. Browse our full catalogue of 143+ research peptides instead.',
                      es: 'No encontramos péptidos que coincidan con tu búsqueda. Explora nuestro catálogo completo de más de 143 péptidos de investigación.',
                      ru: 'Мы не нашли пептиды по вашему запросу. Просмотрите наш полный каталог из 143+ исследовательских пептидов.',
                      de: 'Wir konnten keine passenden Peptide finden. Stöbern Sie stattdessen in unserem vollständigen Katalog mit über 143 Forschungspeptiden.',
                      fr: 'Nous n’avons trouvé aucun peptide correspondant. Parcourez notre catalogue complet de plus de 143 peptides de recherche.',
                      it: 'Non abbiamo trovato peptidi corrispondenti. Sfoglia il nostro catalogo completo di oltre 143 peptidi di ricerca.',
                      pl: 'Nie znaleźliśmy peptydów pasujących do wyszukiwania. Przejrzyj nasz pełny katalog ponad 143 peptydów badawczych.',
                      nl: 'We konden geen overeenkomende peptiden vinden. Bekijk in plaats daarvan onze volledige catalogus van meer dan 143 onderzoekspeptiden.',
                      pt: 'Não encontramos peptídeos correspondentes. Explore nosso catálogo completo com mais de 143 peptídeos de pesquisa.',
                    })[currentLanguage] || 'We couldn’t find any peptides matching your search. Browse our full catalogue instead.'}
                  </p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                    <Button
                      className="rounded-2xl bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white hover:opacity-90"
                      onClick={clearAllFilters}
                      data-testid="empty-search-browse-all"
                    >
                      {({
                        en: 'Browse all 143+ products',
                        es: 'Ver todos los 143+ productos',
                        ru: 'Посмотреть все 143+ товаров',
                        de: 'Alle 143+ Produkte anzeigen',
                        fr: 'Voir les 143+ produits',
                        it: 'Vedi tutti i 143+ prodotti',
                        pl: 'Zobacz wszystkie 143+ produktów',
                        nl: 'Bekijk alle 143+ producten',
                        pt: 'Ver todos os 143+ produtos',
                      })[currentLanguage] || 'Browse all 143+ products'}
                    </Button>
                    <Link to="/blog" className="text-sm text-capsule-teal hover:underline" data-testid="empty-search-blog-link">
                      {({
                        en: 'or read our research blog →',
                        es: 'o lee nuestro blog de investigación →',
                        ru: 'или прочитайте наш блог →',
                        de: 'oder unseren Forschungsblog lesen →',
                        fr: 'ou lisez notre blog de recherche →',
                        it: 'o leggi il nostro blog di ricerca →',
                        pl: 'lub przeczytaj nasz blog badawczy →',
                        nl: 'of lees onze onderzoeksblog →',
                        pt: 'ou leia nosso blog de pesquisa →',
                      })[currentLanguage] || 'or read our research blog →'}
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-600 text-lg mb-4">
                    {LBL.noResults}
                  </p>
                  <Button
                    variant="outline"
                    className="rounded-2xl border-capsule-teal text-capsule-teal hover:bg-capsule-teal/10"
                    onClick={clearAllFilters}
                  >
                    {LBL.clear}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default ProductsPage;
